/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { FunctionComponent, useEffect, useMemo, useReducer, useState } from 'react';
import { types as repr } from '@sitebuilder/core';
import {
    hooks as commonHooks,
    types as commonTypes,
    components as commonComponents,
    utils as commonUtils,
    baseComponents,
    annotations as commonAnnotations,
} from '@sitebuilder/common';
import {
    getLayoutSchemaProperties,
    getStyleSchemaProperties,
    getTextSchemaProperties,
} from '../utils';

const CONTENTS_PLACEHOLDER_ID = 'children';

export interface FilterWithCustomQueryProps {
    dataSourceType: string;
    dataSource: string;
    query: string;
    queryVariables: string;
    transform: string;
    filters: baseComponents.filters.Filter[];
    filtersUI: baseComponents.filters.FiltersUI;
    disableTabStaticData: boolean;
}

export interface WidgetProperties
    extends commonTypes.WidgetPropertiesBase,
        FilterWithCustomQueryProps {}

export interface Widgetprops extends commonTypes.WidgetInstanceProps {
    properties?: any;
}

const FilterWithCustomQuery: FunctionComponent<
    React.PropsWithChildren<commonTypes.WidgetInstanceProps>
> = (props: Widgetprops) => {
    const appContext = commonHooks.useAppContext();
    const { accessToken, originalHostname, queries, baseUrl, graphQLClient, Placeholder } =
        appContext;
    const { data, widgetInstanceId } = props;
    const widgetProperties: any =
        commonHooks.useWidgetProperties<WidgetProperties>(props.widgetInstanceId) ||
        props?.properties;

    const {
        styles,
        dataSourceType,
        dataSource,
        query,
        queryVariables,
        transform,
        filters,
        filtersUI,
        ...rest
    } = widgetProperties || props;
    const theme = props?.data?.themeSelected || props?.theme;
    const variablesStr = queryVariables as string;
    const { disableTabStaticData: globalDisableTabStaticData } =
        commonHooks.useWidgetProperties<WidgetProperties>('root') || {};

    const [variables, setVariables] = useState<any>('');
    const [queryResult, setQueryResult] = useState<repr.GraphQLQueryResult | null>(null);
    const [loading, setLoading] = useState(false); // Track loading state
    const staticDataTabIndex = globalDisableTabStaticData || rest.disableTabStaticData ? -1 : 0;

    const extensions: Record<string, unknown> = {};

    if (transform) {
        extensions.transform = transform;
    }

    const transformFunctionName = `${props.widgetInstanceId}-queryVariables-transform`;
    // Added initialVariable in case state is not update for queryParams variables
    const initialVariable = commonUtils.transformDataWithGlobals(
        transformFunctionName,
        variablesStr,
        data
    );

    useEffect(() => {
        try {
            if (typeof variablesStr === 'string' && variablesStr.length > 0) {
                setVariables(
                    commonUtils.transformDataWithGlobals(transformFunctionName, variablesStr, data)
                );
            }
        } catch (e) {
            console.log(e);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variablesStr]);

    const queryParams = useMemo(
        () => ({
            dataSourceType: dataSourceType,
            dataSetName: dataSource,
            authorizationToken: accessToken as string,
            query,
            variables: variables || initialVariable,
            extensions: extensions as Record<string, unknown>,
            originalHostname,
            baseUrl,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [accessToken, dataSource, dataSourceType, originalHostname, query, variables]
    );

    const queryId = useMemo(() => commonUtils.hashObject(queryParams), [queryParams]);

    useEffect(() => {
        const existingQuery = queries[queryId];
        if (existingQuery?.graphQLQueryResult) setQueryResult(existingQuery?.graphQLQueryResult);
    }, [queries, queryId]);

    // Function to load data
    const loadQueryData = async (graphqlQueryParams: any) => {
        if (loading) return null; // Prevent multiple requests

        setLoading(true);
        try {
            const queryApiClient = graphQLClient;
            const graphQLQueryResult = await queryApiClient.executeQuery(graphqlQueryParams);

            if (graphQLQueryResult?.data && Array.isArray(graphQLQueryResult.data.annotations)) {
                appContext.setters.setAnnotations(
                    commonAnnotations.annotator.Annotator.addAnnotations(
                        graphQLQueryResult.data.annotations,
                        appContext.annotations
                    )
                );
            }
            return graphQLQueryResult;
        } catch (err: any) {
            console.error(err);
            return null;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            const result = await loadQueryData(queryParams);
            // Check if result is not undefined before setting state
            if (result !== undefined) {
                setQueryResult(result);
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryId]);

    const instanceData: any = useMemo(() => {
        const tmp = [queryResult?.data as Record<string, unknown>];
        if (tmp.length === 1 && !tmp[0]) {
            tmp.pop();
        }
        return tmp?.length ? tmp : [{}];
    }, [queryResult?.data]);

    const [dropdownFilters, setDropdownFilters] = useReducer((prevState: any, newState: any) => {
        if (newState === null) {
            // Reset state if new state is null
            return [];
        } else {
            const _dropdownFilters = Object.values(
                [...prevState, newState].reduce((acc, curr) => {
                    acc[curr?.filterBy] = curr;
                    return acc;
                }, [])
            );

            return _dropdownFilters;
        }
    }, []);

    // Dropdown filter change
    const onChangeDropdownOpt = (
        selectedOpts: string,
        _filterBy?: any,
        _label?: string,
        _defaultValue?: string,
        _analyticsLabel?: string,
        filterByKey?: string
    ) => {
        // set dropdown filter value
        setDropdownFilters({ selectedOpts, filterBy: filterByKey });
        // set query variables for the selected dropdown filter
        if (selectedOpts && filterByKey)
            setVariables({
                ...variables,
                [filterByKey]: selectedOpts[0],
            });
    };

    // Text filter change
    const onChangeTextFilter = (
        value: string,
        _filterBy?: string,
        _analyticsLabel?: string,
        filterByKey?: string
    ) => {
        // set query variables for the selected text filter
        if (value && filterByKey) setVariables({ ...variables, [filterByKey]: value });
    };

    const formatDate = (inputDate: any) => {
        let formattedDate = '';
        if (inputDate) {
            // Create a new Date object from the input string
            const date = new Date(inputDate);

            // Get the year, month, and day from the date object
            const year = date.getFullYear();
            // Months are zero-based, so add 1 to get the correct month
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate() + 1).padStart(2, '0');

            // Return the formatted date in yyyy-mm-dd format
            formattedDate = `${year}-${month}-${day}`;
        }

        return formattedDate;
    };

    // Date range filter change
    const onChangeDateRange = (
        minDate: any,
        maxDate: any,
        _filterBy?: string,
        _analyticsLabel?: string,
        startDateFilterByKey?: string,
        endDateFilterByKey?: string
    ) => {
        // set query variables for the selected date filter
        if (startDateFilterByKey && endDateFilterByKey) {
            setVariables({
                ...variables,
                [startDateFilterByKey]: formatDate(minDate),
                [endDateFilterByKey]: formatDate(maxDate),
            });
        }
    };

    // Single date filter change
    const onChangeSingleDate = (
        date: any,
        _filterBy?: string,
        _analyticsLabel?: string,
        dateFilterByKey?: string
    ) => {
        // set query variables for the selected date filter
        if (dateFilterByKey) setVariables({ ...variables, [dateFilterByKey]: formatDate(date) });
    };

    return (
        <div
            css={css`
                ${!theme ? '' : baseComponents.filters.filtersStyles}${getTextSchemaProperties(
                    widgetProperties,
                    data?.colorSpace
                )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                    widgetProperties
                )}${styles || ''}
            `}
            className={widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''}
        >
            <baseComponents.filters.Filters
                data={data}
                filters={filters}
                filtersUI={filtersUI}
                selectedFilters={{ dropdownFilters: dropdownFilters as any }}
                onChangeTextFilter={onChangeTextFilter}
                onSelectOption={onChangeDropdownOpt}
                onClearAllFilters={() => null}
                onCancelFilter={(filter: any) => {
                    setDropdownFilters(null);
                    setVariables({ ...variables, [filter.filterBy]: '' });
                }}
                onChangeDateRange={onChangeDateRange}
                onChangeSingleDate={onChangeSingleDate}
                onClickTextFilter={() => null}
                staticDataTabIndex={staticDataTabIndex}
            />
            <commonComponents.AppContext.AppContextProvider
                {...{
                    ...appContext,
                    queries: {
                        ...appContext.queries,
                        [queryId]: {
                            isQueryLoading: false,
                            graphqlQueryParams: queryParams,
                            graphQLQueryResult: queryResult,
                        },
                    },
                }}
            >
                {widgetInstanceId &&
                    instanceData.map((d: any, i: number) => (
                        <Placeholder
                            key={`${widgetInstanceId}-${i}`}
                            data={{ ...data, ...d }}
                            contentPlaceHolderId={CONTENTS_PLACEHOLDER_ID}
                            parentWidgetInstanceId={widgetInstanceId}
                        />
                    ))}
            </commonComponents.AppContext.AppContextProvider>
        </div>
    );
};

export default FilterWithCustomQuery;
